import type { Car } from './cars/car.entity';
import type { DriveType } from './cars/drive-type.enum';
import type { Make } from './make.entity';
import type { TransmissionType } from './cars/transmission-type.enum';
import type { VehicleCategory } from './vehicle-category.enum';
import type { FuelType } from './cars/fuel-type.enum';
import type { DecommissionedFilter } from './cars/decommissioned-filter.enum';
import type { PaginationParams } from './pagination/pagination-params';
import type { FindAllVehicleTypesSort } from './find-all-vehicle-types-sort.enum';
import type { CustomContractCheckbox } from './custom-contract-checkbox';
import {
  AxaVehicleCategory,
  EuropVehicleCategory,
  JlrVehicleCategory,
  VolvoVehicleCategory,
  type Media as RosMedia,
  type VehicleExtra,
} from '@carvia/ros-client-types';

// OpenAPI inlines rolandCategory literals instead of exporting RolandVehicleCategory.
export enum RolandVehicleCategory {
  BMW_1 = 'BMW_1',
  BMW_2 = 'BMW_2',
  BMW_3 = 'BMW_3',
  BMW_4 = 'BMW_4',
  BMW_5 = 'BMW_5',
  BMW_6 = 'BMW_6',
}

export interface VehicleType {
  id: string;
  make: Make;
  model: string;
  minAge: number;
  seats: number;
  additionalSeats: number;
  fuelType: FuelType;
  transmission: TransmissionType;
  power: number | null;
  mainCategory: VehicleCategory;
  categories: VehicleCategory[];
  accelaration: number | null;
  availableExtras: VehicleExtra[];
  customDeposit: number | null;
  customMinAge: number | null;
  /** Undefined if not ADMIN */
  grossPurchasePrice?: number;
  basePrice: number;
  isAlwaysOnWinterTyres: boolean;
  createdDate: Date;
  updatedDate: Date;
  imageUrl: string;
  customPricePerExtraKm: number | null;
  hasNavigation: boolean | null;
  hasDistanceControl: boolean | null;
  hasRearViewCamera: boolean | null;
  drive: DriveType;
  rangeInKm: number | null;
  tankVolumeInLiter: number | null;
  batteryCapacityInKwh: number | null;
  licenceNeededForXYears: number | null;
  /** Undefined if not ADMIN */
  netPurchasePrice: number | null;
  inspectionIntervalInKm: number;
  discountFrom: string | null;
  discountUntil: string | null;
  discountFactor: number | null;
  customContractCheckboxes: CustomContractCheckbox[];
  axaCategory: AxaVehicleCategory | null;
  rolandCategory: RolandVehicleCategory | null;
  europCategory: EuropVehicleCategory | null;
  volvoCategory: VolvoVehicleCategory | null;
  jlrCategory?: JlrVehicleCategory | null;
  numCars?: number;
  canBeBookedWithDebitCard?: boolean;
  acrissCode?: string | null;
  promotionalVideo?: RosMedia | null;
}

export interface CreateVehicleTypeDto {
  makeId: string;
  model: string;
  minAge: number;
  seats?: number;
  additionalSeats?: number;
  fuelType?: FuelType;
  transmission?: TransmissionType;
  power?: number | null;
  accelaration?: number | null;
  drive?: DriveType;
  tankVolumeInLiter?: number | null;
  batteryCapacityInKwh?: number | null;
  rangeInKm?: number | null;
  isAlwaysOnWinterTyres?: boolean;
  hasNavigation?: boolean | null;
  hasDistanceControl?: boolean | null;
  hasRearViewCamera?: boolean | null;
  customPricePerExtraKm: number | null;
  availableExtraIds: string[];
  customDeposit?: number | null;
  customMinAge?: number | null;
  grossPurchasePrice: number;
  basePrice: number;
  mainCategory: VehicleCategory;
  categories: VehicleCategory[];
  imageFileKey?: string | null;
  inspectionIntervalInKm: number;
  discountFrom?: Date | string | null;
  discountUntil?: Date | string | null;
  discountFactor?: number | null;
  axaCategory?: AxaVehicleCategory | null;
  rolandCategory?: RolandVehicleCategory | null;
  europCategory?: EuropVehicleCategory | null;
  volvoCategory?: VolvoVehicleCategory | null;
  jlrCategory?: JlrVehicleCategory | null;
  acrissCode?: string | null;
  customContractCheckboxes?: CustomContractCheckbox[] | null;
  promotionalVideoMediaId?: string | null;
}

export type UpdateVehicleTypeDto = Partial<CreateVehicleTypeDto>;

export interface FindAllVehicleTypesQuery extends PaginationParams<FindAllVehicleTypesSort> {
  stationIds?: string[];
  startDate?: string;
  endDate?: string;
  decommissioned?: DecommissionedFilter;
  isGhostCar?: boolean;
  query?: string;
  ignoreOpeningHoursAndHolidays?: boolean;
  extended?: boolean;
  includeNumCars?: boolean;
}

export interface GetOneVehicleTypeWithQuery {
  startDate?: string;
  endDate?: string;
  stationId?: string;
  ignoredBookingIds?: string[];
  ignoreOpeningHoursAndHolidays?: boolean;
}

export interface BlockingBookingDto {
  isBlocked?: boolean;
  nextAvailableStartDate?: string;
}

export interface GetAllVehicleTypesCarsDto extends Omit<Car, 'bookings' | 'vehicleType'> {
  isBlocked?: boolean;
}

export interface GetAllVehicleTypesDto extends BlockingBookingDto, Omit<VehicleType, 'cars'> {
  cars: GetAllVehicleTypesCarsDto[];
  minPricePerDay: number | null;
}

export type GetOneVehicleTypeDto = GetAllVehicleTypesDto;
